import { resolveBaseCurrency } from '@/features/order/order-form/lib/instrument-resolution'
import { isFuture, isInverseInstrument, isOption, isPerpetual } from '@/lib/instrument-util'
import { SimulatedInstrument, SimulatedInstrumentType } from '@/state/risk'
import { Denomination, OrderSide, Position } from '@/types/generated/common/trading'
import {
  Exchange,
  Instrument,
  InstrumentKind,
  InstrumentType,
  OptionType,
} from '@/types/generated/common/types'
import {
  BreakEvenSlice,
  RiskSimulationInstrumentType,
  RiskSimulationPosition,
  SimulationGridParams,
  SimulationPriceRangeKind,
} from '@/types/generated/public_api'

// ── Contract type resolution ────────────────────────────────────────────────

/**
 * Resolve the contract type with a reliable fallback.
 * `inst.type` is optional and often unset in the instrument metadata.
 * Fall back to: if settlementCurrency === baseCurrency → INVERSE (coin-margined),
 * otherwise LINEAR (stablecoin-margined).
 */
export function resolveContractType(inst: Instrument): InstrumentType {
  if (inst.type && inst.type !== InstrumentType.UNRECOGNIZED) {
    return inst.type
  }
  return inst.settlementCurrency === inst.baseCurrency
    ? InstrumentType.INSTRUMENT_TYPE_INVERSE
    : InstrumentType.INSTRUMENT_TYPE_LINEAR
}

export function isInversePerpOrFutureInst(inst: Instrument): boolean {
  const inverse = resolveContractType(inst) === InstrumentType.INSTRUMENT_TYPE_INVERSE
  const perp = isPerpetual(inst.symbol)
  const future = inst.kind === InstrumentKind.INSTRUMENT_KIND_FUTURE
  return inverse && (perp || future)
}

export function isInverseOptionInst(inst: Instrument): boolean {
  return (
    resolveContractType(inst) === InstrumentType.INSTRUMENT_TYPE_INVERSE &&
    inst.kind === InstrumentKind.INSTRUMENT_KIND_OPTION
  )
}

// ── Type mapping ────────────────────────────────────────────────────────────

// Perpetuals arrive as INSTRUMENT_KIND_FUTURE (proto has no perpetual kind), and their symbols
// don't always say so (SIM's `XRPPERP`, no hyphen), so a future is dated only when it carries an
// expiry — the same rule `isFuture` applies to positions.
export function getInstrumentSimType(inst: Instrument): SimulatedInstrumentType {
  if (isPerpetual(inst.symbol)) return 'perpetual'
  if (inst.kind === InstrumentKind.INSTRUMENT_KIND_FUTURE) {
    return Number(inst.expirationTsMs ?? 0) > 0 ? 'future' : 'perpetual'
  }
  if (inst.kind === InstrumentKind.INSTRUMENT_KIND_OPTION) {
    return inst.optionType === OptionType.OPTION_TYPE_PUT ? 'put' : 'call'
  }
  return 'perpetual'
}

/**
 * A simulated row's effective type. Rows added before `getInstrumentSimType` checked expiry
 * (still in saved scenarios) can be an undated 'future', which the simulator rejects
 * ("invalid future expiry"): those are perpetuals.
 */
export function resolveSimulatedType(
  inst: Pick<SimulatedInstrument, 'type' | 'expiryDate'>
): SimulatedInstrumentType {
  return inst.type === 'future' && !inst.expiryDate ? 'perpetual' : inst.type
}

// ── Label helpers ───────────────────────────────────────────────────────────

/** Label for the Size field. Inverse Perps/Futures → Total USD Value; all else → crypto amount. */
export function getSizeLabel(inst: Instrument): string {
  if (isInversePerpOrFutureInst(inst)) return `$ (Total USD)`
  return inst.baseCurrency
}

/** Label for the Entry Price field. Inverse Options → base asset premium; all else → USD price. */
export function getEntryPriceLabel(inst: Instrument): string {
  if (isInverseOptionInst(inst)) return `₿ (${inst.baseCurrency})`
  return `$ (USD)`
}

// ── Type display constants ──────────────────────────────────────────────────

export const TYPE_LABEL: Record<SimulatedInstrumentType, string> = {
  perpetual: 'Perp',
  future: 'Fut',
  call: 'Call',
  put: 'Put',
}

export const TYPE_COLOR: Record<SimulatedInstrumentType, string> = {
  perpetual: 'text-cat-blue',
  future: 'text-cat-yellow',
  call: 'text-chart-mint',
  put: 'text-chart-red',
}

// ── Converters ──────────────────────────────────────────────────────────────

export function instrumentToSimulated(
  inst: Instrument,
  size: number,
  entryPrice: number
): SimulatedInstrument {
  const type = getInstrumentSimType(inst)
  let expiryDate: string | undefined
  const tsMs = Number(inst.expirationTsMs ?? 0)
  if (tsMs > 0) expiryDate = new Date(tsMs).toISOString().slice(0, 10)

  return {
    id: `${inst.exchange}:${inst.symbol}`,
    label: inst.symbol,
    asset: inst.baseCurrency,
    exchange: inst.exchange,
    size,
    entryPrice,
    contractType: resolveContractType(inst),
    type,
    strike: inst.strikePrice ? parseFloat(inst.strikePrice) : undefined,
    expiryDate,
    source: 'manual',
  }
}

export function getRiskSimulationPositionSize(
  p: Position,
  instrumentMap: Map<string, Instrument> = new Map()
): number {
  const avgPrice = Number(p.avgPrice)
  const markPrice = Number(p.markPrice ?? 0)
  const referencePrice = avgPrice > 0 ? avgPrice : markPrice

  // `p.quantity` is an unsigned magnitude — direction comes from `p.side`, same
  // convention SizeCell uses (it colors by `side`, not by quantity's sign).
  const rawQuantity = Number(p.quantity)
  const signedQuantity = p.side === OrderSide.ORDER_SIDE_SELL ? -Math.abs(rawQuantity) : rawQuantity

  if (!Number.isFinite(signedQuantity)) return 0

  const inversePerpOrFuture =
    isInverseInstrument(p.symbol, instrumentMap.get(p.symbol)) &&
    (isPerpetual(p.symbol) || isFuture(p)) &&
    !isOption(p)
  const riskSizeIsQuoteNotional =
    inversePerpOrFuture && p.sizeDenomination === Denomination.DENOMINATION_QUOTE

  if (riskSizeIsQuoteNotional) return signedQuantity
  if (p.sizeDenomination === Denomination.DENOMINATION_QUOTE) {
    return referencePrice > 0 ? signedQuantity / referencePrice : signedQuantity
  }

  if (inversePerpOrFuture && p.sizeDenomination === Denomination.DENOMINATION_BASE) {
    return referencePrice > 0 ? signedQuantity * referencePrice : signedQuantity
  }

  return signedQuantity
}

export function positionToSimulated(
  p: Position,
  instrumentMap: Map<string, Instrument> = new Map()
): SimulatedInstrument {
  const perp = isPerpetual(p.symbol)
  const opt = isOption(p)
  const future = isFuture(p)
  const tsMs = Number(p.expiry)
  const expiryDate = tsMs && tsMs > 0 ? new Date(tsMs).toISOString().slice(0, 10) : undefined

  let type: SimulatedInstrumentType = 'perpetual'
  if (future) type = 'future'
  else if (opt) type = p.symbol.includes('-C') ? 'call' : 'put'

  const avgPrice = Number(p.avgPrice)
  const markPrice = Number(p.markPrice ?? 0)
  const entryPrice = avgPrice > 0 ? avgPrice : markPrice

  const size = getRiskSimulationPositionSize(p, instrumentMap)

  return {
    id: `${p.accountId}:${p.symbol}`,
    label: p.symbol,
    asset: resolveBaseCurrency(instrumentMap, p.symbol),
    exchange: p.exchange,
    accountId: p.accountId,
    size,
    entryPrice,
    contractType: isInverseInstrument(p.symbol, instrumentMap.get(p.symbol))
      ? InstrumentType.INSTRUMENT_TYPE_INVERSE
      : InstrumentType.INSTRUMENT_TYPE_LINEAR,
    type,
    strike: p.strike ? Number(p.strike) : undefined,
    expiryDate,
    source: 'position',
  }
}

/**
 * Which asset a simulation opens on: the first of `preferred` the legs actually hold (e.g. a
 * linked ticker, then the filter's current coin), else the asset with the largest summed
 * notional (|size × entry price|). Null when there are no legs. Shared by the page's first
 * auto-run and the widget's first seed, so both land on the same asset.
 */
export function pickStartingAsset(
  legs: readonly Pick<SimulatedInstrument, 'asset' | 'size' | 'entryPrice'>[],
  preferred: readonly (string | null | undefined)[] = []
): string | null {
  const notionalByAsset = new Map<string, number>()
  for (const leg of legs) {
    const notional = Math.abs(leg.size * leg.entryPrice)
    notionalByAsset.set(leg.asset, (notionalByAsset.get(leg.asset) ?? 0) + notional)
  }
  const held = preferred.find((asset): asset is string => !!asset && notionalByAsset.has(asset))
  if (held) return held
  let best: string | null = null
  let bestNotional = -Infinity
  for (const [asset, notional] of notionalByAsset) {
    if (notional > bestNotional) {
      best = asset
      bestNotional = notional
    }
  }
  return best
}

// ── RiskSimulationRequest conversion ────────────────────────────────────────
//
// Two separate converters, not one — they resolve expiry at different precision and must
// not be collapsed into a single path (doing so would corrupt the live-position branch's
// simulated option/future expiry, see risk-slides-table.tsx usage).

/**
 * Converts a scenario-authored simulated instrument row to the wire shape. Only ever has
 * day-granularity expiry (`SimulatedInstrument.expiryDate` is a date string), so reconstructing
 * a fixed 08:00 UTC time here loses nothing.
 */
export function simulatedInstrumentToRiskSimulationPosition(
  inst: SimulatedInstrument
): RiskSimulationPosition {
  const expiryStr = inst.expiryDate ? new Date(inst.expiryDate + 'T08:00:00Z').toISOString() : ''
  const type = resolveSimulatedType(inst)
  const instrumentType: RiskSimulationInstrumentType =
    type === 'future'
      ? { future: { expiry: expiryStr } }
      : type === 'call'
        ? { call: { strike: inst.strike ?? 0, expiry: expiryStr } }
        : type === 'put'
          ? { put: { strike: inst.strike ?? 0, expiry: expiryStr } }
          : { perpetual: {} }
  return {
    asset: inst.asset,
    size: inst.size,
    entryPrice: inst.entryPrice,
    contractType: inst.contractType,
    instrumentType,
    accountId: inst.accountId,
  }
}

/**
 * Converts a live open position to the wire shape, at full millisecond expiry precision
 * (unlike `positionToSimulated`'s own `expiryDate`, which is truncated to a date string for
 * display purposes) — do not route this through `simulatedInstrumentToRiskSimulationPosition`,
 * it would silently reconstruct a fake 08:00 UTC expiry from the truncated date instead of the
 * position's real expiry timestamp.
 */
export function positionToRiskSimulationPosition(
  p: Position,
  instrumentMap: Map<string, Instrument>
): RiskSimulationPosition {
  const perp = isPerpetual(p.symbol)
  const opt = isOption(p)
  const future = isFuture(p)
  const tsMs = Number(p.expiry)
  const tsStr = tsMs && tsMs > 0 ? new Date(tsMs).toISOString() : ''
  const instrumentType: RiskSimulationInstrumentType = perp
    ? { perpetual: {} }
    : future
      ? { future: { expiry: tsStr } }
      : opt
        ? p.symbol.includes('-C')
          ? { call: { strike: Number(p.strike), expiry: tsStr } }
          : { put: { strike: Number(p.strike), expiry: tsStr } }
        : { perpetual: {} }

  const simulated = positionToSimulated(p, instrumentMap)

  return {
    asset: simulated.asset,
    size: simulated.size,
    entryPrice: simulated.entryPrice,
    contractType: simulated.contractType,
    instrumentType,
    accountId: p.accountId,
  }
}

// ── Portfolio reconciliation ────────────────────────────────────────────────

/**
 * Merges freshly-seeded `'position'` rows into the existing simulated portfolio.
 * A `'position'` row is dropped if it's no longer present in `seeded` (its
 * position closed, or the account it belonged to is no longer connected);
 * `'manual'` rows are left untouched regardless of `seeded`'s contents.
 */
export function reconcilePositionSourcedPortfolio(
  prev: SimulatedInstrument[],
  seeded: SimulatedInstrument[]
): { next: SimulatedInstrument[]; changed: boolean } {
  const seededIds = new Set(seeded.map((sim) => sim.id))
  const byId = new Map(prev.map((row) => [row.id, row]))
  let changed = false

  for (const row of prev) {
    if (row.source === 'position' && !seededIds.has(row.id)) {
      byId.delete(row.id)
      changed = true
    }
  }

  for (const sim of seeded) {
    const existing = byId.get(sim.id)
    if (!existing || existing.size !== sim.size || existing.entryPrice !== sim.entryPrice) {
      byId.set(sim.id, sim)
      changed = true
    }
  }

  return { next: changed ? [...byId.values()] : prev, changed }
}

// ── Saved scenarios ─────────────────────────────────────────────────────────

/** True for an option leg (call/put) whose expiry has already passed as of `today`. */
export function isExpiredLeg(inst: SimulatedInstrument, today: string): boolean {
  if (inst.type !== 'call' && inst.type !== 'put') return false
  if (!inst.expiryDate) return false
  return inst.expiryDate < today
}

/**
 * Drops expired option legs from a saved scenario before it's restored — a
 * saved snapshot can outlive the options it held, and simulating an expired
 * leg doesn't mean anything. `today` is passed in (YYYY-MM-DD) rather than
 * computed here so callers can test deterministically.
 */
export function pruneExpiredLegs(
  portfolio: SimulatedInstrument[],
  today: string
): { next: SimulatedInstrument[]; removedCount: number } {
  const next = portfolio.filter((inst) => !isExpiredLeg(inst, today))
  return { next, removedCount: portfolio.length - next.length }
}

// ── Cross-exchange asset options ────────────────────────────────────────────

export type AssetOption = {
  baseCurrency: string
  exchanges: Exchange[]
}

/**
 * Union of base currencies across every given exchange's FUTURE/OPTION
 * instruments — the set of assets a Risk Slides simulation can run against.
 * SPOT/INDEX/OTHER kinds are excluded since Risk Slides only simulates
 * instruments with Greeks (perpetuals, dated futures, options). Callers pass
 * every exchange Risk Slides supports (not just ones with a connected
 * account) so simulation stays available regardless of account connectivity.
 */
export function buildAssetOptions(
  exchanges: Exchange[],
  multiExchangeInstrumentMap: Map<Exchange, Map<string, Instrument>>
): AssetOption[] {
  const byBase = new Map<string, Set<Exchange>>()

  for (const exchange of exchanges) {
    const instruments = multiExchangeInstrumentMap.get(exchange)
    if (!instruments) continue

    for (const inst of instruments.values()) {
      if (
        inst.kind !== InstrumentKind.INSTRUMENT_KIND_FUTURE &&
        inst.kind !== InstrumentKind.INSTRUMENT_KIND_OPTION
      ) {
        continue
      }
      const base = inst.baseCurrency?.toUpperCase()
      if (!base) continue
      if (!byBase.has(base)) byBase.set(base, new Set())
      byBase.get(base)!.add(exchange)
    }
  }

  return [...byBase.entries()]
    .map(([baseCurrency, exchanges]) => ({ baseCurrency, exchanges: [...exchanges] }))
    .sort((a, b) => a.baseCurrency.localeCompare(b.baseCurrency))
}

/**
 * Find a perpetual instrument for `baseCurrency`'s live price feed, preferring
 * `preferredExchange`'s list (keeps the existing single-exchange price behavior
 * for the common case) and falling back to any other exchange's list — needed so
 * a cross-exchange-only asset (e.g. one only listed on Hyperliquid, not the
 * globally-selected account's exchange) can still resolve a price.
 */
// ── Price-step resolution ───────────────────────────────────────────────────

/** Fallback division count when the step selection can't be parsed. */
export const DEFAULT_PRICE_STEPS = 10
/** Mirror of the lambda's `MAX_PRICE_STEPS` (100 columns); it widens the step to fit. */
export const MAX_PRICE_STEPS = 99

type SimulationPriceGrid = Pick<
  SimulationGridParams,
  'valueLower' | 'valueUpper' | 'priceRangeKind'
>

/**
 * Recover the mark price the server used to turn a percent grid into absolute
 * response prices. The response contains only absolute `SimulationPoint.price`
 * values, so each endpoint is inverted from `price = mark * (1 + offset)` and
 * the two estimates are averaged to absorb server-side rounding.
 *
 * Absolute grids have no percent reference; retain the legacy middle-price ATM.
 */
export function resolveSimulationReferencePrice(
  prices: number[],
  grid: SimulationPriceGrid | null | undefined
): number | undefined {
  if (prices.length === 0) return undefined

  const fallback = prices[Math.floor(prices.length / 2)]
  if (grid?.priceRangeKind !== SimulationPriceRangeKind.SIMULATION_PRICE_RANGE_KIND_PERCENT) {
    return fallback
  }

  let minPrice = Number.POSITIVE_INFINITY
  let maxPrice = Number.NEGATIVE_INFINITY
  for (const price of prices) {
    if (!Number.isFinite(price)) continue
    minPrice = Math.min(minPrice, price)
    maxPrice = Math.max(maxPrice, price)
  }

  const estimates: number[] = []
  const lowerScale = 1 + grid.valueLower
  const upperScale = 1 + grid.valueUpper
  if (lowerScale > 0 && Number.isFinite(minPrice)) estimates.push(minPrice / lowerScale)
  if (upperScale > 0 && Number.isFinite(maxPrice)) estimates.push(maxPrice / upperScale)

  const validEstimates = estimates.filter((estimate) => Number.isFinite(estimate) && estimate > 0)
  if (validEstimates.length === 0) return fallback
  return validEstimates.reduce((sum, estimate) => sum + estimate, 0) / validEstimates.length
}

// ── Break-evens ─────────────────────────────────────────────────────────────

/**
 * Index `RiskSimulationResponse.break_evens` by the `SimulationPoint.timestamp` they
 * belong to. One list covers both USD and base results (base PnL is USD PnL / price,
 * so both cross zero at the same prices). Keyed by epoch ms, not the raw string, so
 * a differently-formatted RFC3339 (fractional seconds, `Z` vs `+00:00`) still matches.
 */
export function indexBreakEvens(slices: BreakEvenSlice[] | undefined): Map<number, number[]> {
  const byTime = new Map<number, number[]>()
  for (const slice of slices ?? []) {
    const ms = Date.parse(slice.timestamp)
    if (!Number.isFinite(ms)) continue
    byTime.set(
      ms,
      slice.prices.filter((p) => Number.isFinite(p)).sort((a, b) => a - b)
    )
  }
  return byTime
}

export function breakEvensAt(
  index: Map<number, number[]>,
  timestamp: string
): number[] | undefined {
  return index.get(Date.parse(timestamp))
}

/**
 * Fractional grid index of `price` on an ascending price grid (2 between index 2 and 3
 * → 2.x), for placing a break-even between two grid columns on an index-based axis.
 * Outside the grid → undefined.
 */
export function priceToGridPosition(price: number, gridPrices: number[]): number | undefined {
  const n = gridPrices.length
  if (n === 0 || price < gridPrices[0] || price > gridPrices[n - 1]) return undefined
  for (let i = 0; i < n - 1; i++) {
    const lo = gridPrices[i]
    const hi = gridPrices[i + 1]
    if (price <= hi) return hi === lo ? i : i + (price - lo) / (hi - lo)
  }
  return n - 1
}

/**
 * Group break-even prices under the grid column whose price is closest to each one
 * (the grid is sorted ascending), so a table can show them in place. Two break-evens
 * nearest the same column share it.
 */
export function assignBreakEvensToColumns(
  breakEvens: number[],
  gridPrices: number[]
): Map<number, number[]> {
  const byColumn = new Map<number, number[]>()
  if (gridPrices.length === 0) return byColumn
  for (const be of breakEvens) {
    let best = 0
    for (let i = 1; i < gridPrices.length; i++) {
      if (Math.abs(gridPrices[i] - be) < Math.abs(gridPrices[best] - be)) best = i
    }
    const list = byColumn.get(best)
    if (list) list.push(be)
    else byColumn.set(best, [be])
  }
  return byColumn
}

/** Multipliers tried, largest first, when densifying a grid for the chart. */
const CHART_DENSIFY_FACTORS = [4, 3, 2] as const

/**
 * A denser price grid for the chart only, so its curves read smooth: below 100 columns
 * (`steps + 1`), the requested step count times the largest of 4 / 3 / 2 that stays within
 * the lambda's `MAX_PRICE_STEPS`. E.g. −20%…+20% at 1% = 40 steps → 80. The table keeps
 * the requested steps; this never feeds it.
 */
export function densifyPriceStepsForChart(steps: number): number {
  if (steps + 1 >= 100) return steps
  const factor = CHART_DENSIFY_FACTORS.find((k) => steps * k <= MAX_PRICE_STEPS)
  return factor ? steps * factor : steps
}

/**
 * Translate a `priceStep` dropdown selection into the API's `price_steps` count
 * (the number of grid divisions; the API emits count + 1 rows).
 *
 * The dropdown mixes two option shapes with opposite meanings:
 *   - "N Parts" → N divisions directly (the value IS the count).
 *   - "X%"      → a per-step *size* of X%, so the count is the range width
 *                 divided by the step size. In percent mode the bounds are
 *                 already percentages; in absolute mode we convert with the range
 *                 midpoint as the spot proxy (the abs range is centred on spot).
 *
 * `rangeFrom`/`rangeTo` are the raw filter bounds — percentages (e.g. -20, 20)
 * in percent mode, absolute prices otherwise.
 */
export function resolvePriceSteps(
  priceStep: string,
  rangeFrom: number,
  rangeTo: number,
  isPercent: boolean
): number {
  const value = parseFloat(priceStep)
  if (!Number.isFinite(value) || value <= 0) return DEFAULT_PRICE_STEPS

  // "N Parts" — the value already IS the division count.
  if (!priceStep.includes('%')) return Math.max(1, Math.round(value))

  // "X%" — value is a per-step size in percent; derive the division count.
  const width = Math.abs(rangeTo - rangeFrom)
  const stepWidth = isPercent
    ? value // both range width and step are in percent units
    : Math.abs((rangeFrom + rangeTo) / 2) * (value / 100) // abs: X% of midpoint (~spot)
  if (!Number.isFinite(stepWidth) || stepWidth <= 0) return DEFAULT_PRICE_STEPS

  const steps = Math.round(width / stepWidth)
  return Math.min(MAX_PRICE_STEPS, Math.max(1, steps))
}

export function findPerpInstrumentForPrice(
  baseCurrency: string,
  preferredExchange: Exchange,
  multiExchangeInstrumentMap: Map<Exchange, Map<string, Instrument>>
): Instrument | undefined {
  const base = baseCurrency.toUpperCase()
  const matchesBase = (inst: Instrument) =>
    inst.symbol.toLowerCase().includes('-perp') &&
    (inst.baseCurrency?.toUpperCase() === base || inst.symbol.toUpperCase().startsWith(base + '-'))

  const preferred = multiExchangeInstrumentMap.get(preferredExchange)
  const preferredMatch = preferred && [...preferred.values()].find(matchesBase)
  if (preferredMatch) return preferredMatch

  for (const [exchange, instruments] of multiExchangeInstrumentMap) {
    if (exchange === preferredExchange) continue
    const match = [...instruments.values()].find(matchesBase)
    if (match) return match
  }
  return undefined
}

// ── Simulation date range ───────────────────────────────────────────────────

const RISK_DAY_MS = 86400 * 1000
/** How far To reaches when no expiry lies ahead (perp-only) or after a picked From. */
const FALLBACK_RANGE_DAYS = 7

/** A local calendar date (what the date pickers work in) → YYYY-MM-DD. */
export const localDateToDay = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

/** YYYY-MM-DD → a local calendar date for the date pickers. */
export const dayToLocalDate = (day: string) => {
  const [y, m, d] = day.split('-').map(Number)
  return new Date(y, m - 1, d)
}

/** A simulation day is sent as 08:00 UTC (expiry time). */
export const dayToIso = (day: string) => `${day}T08:00:00.000Z`

const addDays = (day: string, days: number) =>
  new Date(Date.parse(dayToIso(day)) + days * RISK_DAY_MS).toISOString().slice(0, 10)

/** Whether any of the simulated legs' expiries lies ahead — i.e. the From–To range is
 * the user's to choose. A perp-only portfolio has none. */
export function hasUpcomingExpiry(expiryDays: readonly (string | null | undefined)[]): boolean {
  const today = localDateToDay(new Date())
  return expiryDays.some((d) => !!d && d >= today)
}

/**
 * The simulation's From–To days (YYYY-MM-DD). An unset bound is automatic: From is today,
 * To is the farthest expiry among `expiryDays` (the simulated futures/options' expiries).
 * With no expiry ahead (a perp-only portfolio) the range is fixed to today plus a week and
 * any picked dates are ignored — the toolbar disables its date fields then. To never
 * precedes From.
 */
export function resolveRiskDateRange(
  startDate: Date | undefined,
  endDate: Date | undefined,
  expiryDays: readonly (string | null | undefined)[]
): { startDay: string; endDay: string } {
  if (!hasUpcomingExpiry(expiryDays)) {
    const today = localDateToDay(new Date())
    return { startDay: today, endDay: addDays(today, FALLBACK_RANGE_DAYS) }
  }
  const startDay = localDateToDay(startDate ?? new Date())
  let endDay = endDate ? localDateToDay(endDate) : undefined
  if (!endDay) {
    const ahead = expiryDays.filter((d): d is string => !!d && d > startDay).sort()
    endDay = ahead[ahead.length - 1] ?? addDays(startDay, FALLBACK_RANGE_DAYS)
  }
  return { startDay, endDay: endDay < startDay ? startDay : endDay }
}

// ── Coin ↔ row selection ────────────────────────────────────────────────────
//
// A simulation covers one asset, so the page's coin picker and its row checkboxes behave like
// a select-all checkbox over groups: the coin is the group's master (picking it checks every
// row of that asset and unchecks every other asset's), and a row is a child (checking one of
// another asset moves the coin there and clears the other assets, leaving its siblings as
// they were).

/**
 * Picks `asset` as the master: every held leg of it (`positionLegs`, keeping any simulated
 * Size / Entry Price already edited on a picked one) and every manual row of it are checked;
 * other assets' position picks are dropped and their manual rows unchecked.
 */
export function selectPortfolioAsset(
  portfolio: SimulatedInstrument[],
  asset: string,
  positionLegs: SimulatedInstrument[]
): SimulatedInstrument[] {
  const pickedById = new Map(
    portfolio.filter((inst) => inst.source === 'position').map((inst) => [inst.id, inst])
  )
  return [
    ...portfolio
      .filter((inst) => inst.source === 'manual')
      .map((inst) => withExcluded(inst, inst.asset !== asset)),
    ...positionLegs
      .filter((leg) => leg.asset === asset)
      .map((leg) => pickedById.get(leg.id) ?? leg),
  ]
}

/**
 * Checks one row as a child: `row` is added (or re-checked) and every other asset's rows are
 * cleared, while the rows of its own asset keep their state.
 */
export function checkPortfolioRow(
  portfolio: SimulatedInstrument[],
  row: SimulatedInstrument
): SimulatedInstrument[] {
  return [
    ...portfolio
      .filter((inst) => inst.id !== row.id)
      .filter((inst) => inst.source === 'manual' || inst.asset === row.asset)
      .map((inst) => (inst.asset === row.asset ? inst : withExcluded(inst, true))),
    { ...row, excluded: false },
  ]
}

function withExcluded(inst: SimulatedInstrument, excluded: boolean): SimulatedInstrument {
  return !!inst.excluded === excluded ? inst : { ...inst, excluded }
}
